import React, { useEffect, useMemo, useState } from 'react';
import { ConfigProvider, Modal, Button, Input, Segmented, Spin, Tooltip } from 'antd';
import { FiSearch, FiLock, FiEyeOff, FiHelpCircle } from 'react-icons/fi';
import styles from '../SidebarAccessModal/SidebarAccessModal.module.scss';
import ownStyles from './FormQuestionsModal.module.scss';
import { Check, Toggle, usePortalColor, tint, hostOf, initialsOf } from '../SidebarAccessModal/SidebarAccessModal';
import { FORM_QUESTIONS, normalizeFormQuestions, buildFormQuestionsPayload } from '../../../constant/FormQuestions';

const sameEntry = (a = {}, b = {}) =>
    Boolean(a.visible) === Boolean(b.visible) && Boolean(a.required) === Boolean(b.required);

/**
 * Admin dialog to choose, per form, which questions are shown and which are required on a white label portal.
 * The list of forms and questions comes from constant/FormQuestions.js.
 *
 * Props:
 *  - open            boolean
 *  - customer        white label customer row (.id, .url, .name, .profile_logo, .homepage_logo, .form_questions)
 *  - onSubmit(payload) receives { version, forms: { [formKey]: { [questionKey]: { visible, required } } } }
 *                    (the shape stored in website_type.form_questions); should return a promise
 *  - onCancel()
 */
const FormQuestionsModal = ({ open, customer, onSubmit, onCancel }) => {
    const [saved, setSaved] = useState({});
    const [forms, setForms] = useState({});
    const [formKey, setFormKey] = useState(FORM_QUESTIONS[0].key);
    const [submitting, setSubmitting] = useState(false);
    const [search, setSearch] = useState('');
    const themeColor = usePortalColor(customer);

    useEffect(() => {
        if (open) {
            const initial = normalizeFormQuestions(customer?.form_questions);
            setSaved(initial);
            setForms(initial);
            setFormKey(FORM_QUESTIONS[0].key);
            setSubmitting(false);
            setSearch('');
        }
    }, [open, customer]);

    const form = FORM_QUESTIONS.find(({ key }) => key === formKey) || FORM_QUESTIONS[0];
    const settings = forms[form.key] || {};
    const editable = form.questions.filter(q => !q.locked);

    /* ---------- mutations ---------- */
    const setQuestion = (questionKey, patch) =>
        setForms(prev => {
            const entry = { ...prev[form.key]?.[questionKey], ...patch };
            if (!entry.visible) entry.required = false;
            return { ...prev, [form.key]: { ...prev[form.key], [questionKey]: entry } };
        });

    const setAllVisible = (visible) =>
        setForms(prev => {
            const next = { ...prev[form.key] };
            editable.forEach(({ key }) => {
                next[key] = { visible, required: visible && Boolean(next[key]?.required) };
            });
            return { ...prev, [form.key]: next };
        });

    /* ---------- derived ---------- */
    const changedCount = useMemo(
        () => FORM_QUESTIONS.reduce(
            (count, f) => count + f.questions.filter(({ key }) => !sameEntry(forms[f.key]?.[key], saved[f.key]?.[key])).length,
            0
        ),
        [forms, saved]
    );
    const hasChanges = changedCount > 0;

    const total = form.questions.length;
    const visibleCount = form.questions.filter(({ key }) => settings[key]?.visible).length;
    const requiredCount = form.questions.filter(({ key }) => settings[key]?.required).length;
    const allVisible = editable.every(({ key }) => settings[key]?.visible);
    const someVisible = editable.some(({ key }) => settings[key]?.visible);

    const filteredQuestions = useMemo(() => {
        const q = search.trim().toLowerCase();
        return q ? form.questions.filter(({ label }) => label.toLowerCase().includes(q)) : form.questions;
    }, [search, form]);

    const isConfigured = customer?.form_questions !== null && customer?.form_questions !== undefined;
    const host = hostOf(customer?.url);
    const logo = customer?.profile_logo || customer?.homepage_logo;

    /* ---------- actions ---------- */
    const handleSave = async () => {
        setSubmitting(true);
        try {
            await onSubmit(buildFormQuestionsPayload(forms));
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        if (!submitting) onCancel();
    };

    return (
        <ConfigProvider theme={{ token: { colorPrimary: themeColor, borderRadius: 6, fontFamily: "'Poppins', sans-serif" } }}>
            <Modal
                className={styles.modal}
                style={{ '--portal-color': themeColor }}
                open={open}
                onCancel={handleCancel}
                width={820}
                centered
                maskClosable={!submitting}
                destroyOnHidden
                title={
                    <div className={styles.header}>
                        <div className={styles.avatar} style={{ background: tint(themeColor, 0.14), color: themeColor }}>
                            {logo ? <img src={logo} alt="" /> : initialsOf(customer?.name)}
                        </div>
                        <div className={styles.headerText}>
                            <div className={styles.headerTitle}>Form Questions</div>
                            <div className={styles.headerSub}>
                                {customer?.name ? <span className={styles.portalName}>{customer.name}</span> : null}
                                {customer?.name && host ? <span className={styles.sep}>·</span> : null}
                                {host ? <span>{host}</span> : null}
                            </div>
                        </div>
                        <span className={`${styles.badge} ${isConfigured ? styles.badgeCustom : styles.badgeDefault}`}>
                            {isConfigured ? 'Custom' : 'Default'}
                        </span>
                    </div>
                }
                footer={
                    <div className={styles.footer}>
                        <span className={`${styles.footerNote} ${hasChanges ? styles.footerNoteActive : ''}`}>
                            {hasChanges
                                ? `${changedCount} unsaved ${changedCount === 1 ? 'change' : 'changes'}`
                                : 'No unsaved changes'}
                        </span>
                        <div className={styles.footerButtons}>
                            <Button onClick={handleCancel} disabled={submitting}>Cancel</Button>
                            <Button type="primary" onClick={handleSave} loading={submitting} disabled={!hasChanges}>
                                Save changes
                            </Button>
                        </div>
                    </div>
                }
            >
                <Spin spinning={submitting}>
                    {FORM_QUESTIONS.length > 1 && (
                        <Segmented
                            block
                            className={ownStyles.formTabs}
                            value={form.key}
                            onChange={setFormKey}
                            options={FORM_QUESTIONS.map(({ key, label }) => ({ value: key, label }))}
                        />
                    )}

                    <div className={styles.toolbar}>
                        <Input
                            allowClear
                            className={styles.search}
                            classNames={{ input: 'browser-default' }}
                            prefix={<FiSearch className={styles.searchIcon} />}
                            placeholder="Search questions"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        <div className={styles.toolbarRight}>
                            <span className={styles.count}>
                                <strong style={{ color: themeColor }}>{visibleCount}</strong> of {total} shown
                                {' · '}
                                <strong style={{ color: themeColor }}>{requiredCount}</strong> required
                            </span>
                            <span className={styles.vr} />
                            <Button type="link" size="small" onClick={() => setAllVisible(true)}>Show all</Button>
                            <Button type="link" size="small" onClick={() => setAllVisible(false)}>Hide all</Button>
                        </div>
                    </div>

                    <div className={styles.tableWrapper}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th className={`${styles.linkCol} ${ownStyles.questionCol}`}>
                                        <span className={styles.thLabel}>{form.label}</span>
                                    </th>
                                    <th className={styles.allCol}>
                                        <div className={styles.thStack}>
                                            <Check
                                                label="Shown"
                                                color={themeColor}
                                                checked={allVisible}
                                                indeterminate={!allVisible && someVisible}
                                                onChange={setAllVisible}
                                            />
                                            <span className={styles.thCount}>{visibleCount}/{total}</span>
                                        </div>
                                    </th>
                                    <th className={styles.roleCol}>
                                        <Tooltip title="Required questions must be answered before the form can be submitted" mouseEnterDelay={0.4}>
                                            <div className={styles.thStack}>
                                                <span className={styles.checkLabel}>Required</span>
                                                <span className={styles.thCount}>{requiredCount}/{total}</span>
                                            </div>
                                        </Tooltip>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredQuestions.length === 0 && (
                                    <tr>
                                        <td colSpan={3} className={styles.empty}>
                                            <FiHelpCircle /> No questions match "{search}"
                                        </td>
                                    </tr>
                                )}
                                {filteredQuestions.map(({ key, label, locked, requirable }) => {
                                    const entry = settings[key] || {};
                                    const visible = Boolean(entry.visible);
                                    const changed = !sameEntry(entry, saved[form.key]?.[key]);
                                    return (
                                        <tr key={`row-${key}`} className={`${styles.row} ${visible ? '' : styles.rowOff}`}>
                                            <td className={`${styles.linkCol} ${ownStyles.questionCol}`}>
                                                <div className={styles.linkCell}>
                                                    <span className={`${styles.linkLabel} ${ownStyles.questionLabel}`}>{label}</span>
                                                    {locked && (
                                                        <Tooltip title="Core question, always shown and required">
                                                            <span className={styles.lockTag}><FiLock /> Always</span>
                                                        </Tooltip>
                                                    )}
                                                    {!visible && (
                                                        <span className={styles.lockTag}><FiEyeOff /> Hidden</span>
                                                    )}
                                                    {changed && <span className={styles.changedDot} style={{ background: themeColor }} />}
                                                </div>
                                            </td>
                                            <td className={styles.allCol}>
                                                {locked ? null : (
                                                    <Toggle
                                                        ariaLabel={`Show "${label}"`}
                                                        color={themeColor}
                                                        checked={visible}
                                                        onChange={checked => setQuestion(key, { visible: checked })}
                                                    />
                                                )}
                                            </td>
                                            <td className={styles.roleCol}>
                                                {locked ? (
                                                    <FiLock className={ownStyles.lockIcon} />
                                                ) : requirable ? (
                                                    <span className={visible ? '' : ownStyles.disabled}>
                                                        <Check
                                                            ariaLabel={`Require "${label}"`}
                                                            color={themeColor}
                                                            checked={Boolean(entry.required)}
                                                            onChange={checked => visible && setQuestion(key, { required: checked })}
                                                        />
                                                    </span>
                                                ) : (
                                                    <span className={ownStyles.na}>—</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <p className={styles.help}>
                        Hidden questions are removed from the form for everyone on this portal. Some questions are still
                        limited by role or plan (e.g. subtitles, YouTube helper). Changes take effect the next time the portal is opened.
                    </p>
                </Spin>
            </Modal>
        </ConfigProvider>
    );
};

export default FormQuestionsModal;
